/**
 * Microsoft Entra sign-in and Microsoft 365 data consent — the pure half of slice B
 * (design 20-identity-design.md §4.2, 40-client-apps-design.md §1.3/§2.3).
 *
 * What lives here is what both the API and a frontend need to agree on: the authorize
 * and admin-consent URLs, which scopes each data purpose asks for, the redirect URIs each
 * environment's Entra app must have registered, the admin role ids, and the Dataverse
 * environment-URL rule. Anything that holds a secret or calls Microsoft is in the API
 * (`apps/api/worker/src/services/microsoft/`).
 *
 * Vendor facts below were read on Microsoft's documentation, not captured from a live
 * tenant; the ones the design still lists as LIVE CHECK (§12 B) are marked so here.
 */
import { MICROSOFT_PERSONAL_TENANT_ID } from './identity'
import { apiHostOf, clientRedirectUri, type ClientConnectResult, type ClientEnvironment } from './clientApps'

export const MICROSOFT_LOGIN_ORIGIN = 'https://login.microsoftonline.com'

/**
 * The authority every sign-in uses: work and school accounts of any organisation, and
 * personal Microsoft accounts (outlook.com, hotmail, live), since 2026-10-17. A work
 * account signs in to its organisation's `ms:<tid>` account; a personal one (its token's
 * tid is MICROSOFT_PERSONAL_TENANT_ID) to the **email** account of its verified address,
 * exactly as a Google account without `hd` does. The app registration's signInAudience
 * must be AzureADandPersonalMicrosoftAccount for personal accounts to get this far.
 */
export const MICROSOFT_SIGNIN_AUTHORITY = 'common'

/**
 * Work and school accounts only: admin consent (an organisation's admin approving the
 * app) has no meaning for a personal account, and `/common/adminconsent` is refused.
 */
export const MICROSOFT_ORGANIZATIONS_AUTHORITY = 'organizations'

/** `https://login.microsoftonline.com/<tenant>/oauth2/v2.0/authorize` */
export function microsoftAuthorizeEndpoint(tenant: string = MICROSOFT_SIGNIN_AUTHORITY): string {
  return `${MICROSOFT_LOGIN_ORIGIN}/${tenant}/oauth2/v2.0/authorize`
}

/** `https://login.microsoftonline.com/<tenant>/oauth2/v2.0/token` — also the client assertion's `aud`. */
export function microsoftTokenEndpoint(tenant: string = MICROSOFT_SIGNIN_AUTHORITY): string {
  return `${MICROSOFT_LOGIN_ORIGIN}/${tenant}/oauth2/v2.0/token`
}

/** The issuer a v2.0 token from tenant `tid` must carry (multi-tenant: checked per tid). */
export function microsoftIssuer(tid: string): string {
  return `${MICROSOFT_LOGIN_ORIGIN}/${tid}/v2.0`
}

/** Entra's signing keys for v2.0 tokens of every tenant (no per-app keys: we use no claims mapping). */
export const MICROSOFT_JWKS_URL = `${MICROSOFT_LOGIN_ORIGIN}/common/discovery/v2.0/keys`

/**
 * Sign-in asks for these and nothing else (decision §11.6): the first consent screen a
 * customer's employee sees should be the smallest one Microsoft has. Data scopes are
 * asked later, per purpose.
 */
export const MICROSOFT_SIGNIN_SCOPES = ['openid', 'profile', 'email', 'offline_access'] as const

export const GRAPH_ORIGIN = 'https://graph.microsoft.com'
/** The global discovery service: which Dataverse environments the person can reach. */
export const DATAVERSE_DISCOVERY_ORIGIN = 'https://globaldisco.crm.dynamics.com'

/**
 * What a person connects, each asked incrementally (§4.2's table):
 * - `files` — Excel workbooks in OneDrive / SharePoint, and the output saved next to them;
 * - `sites` — SharePoint Lists: items, attachments, status/link columns;
 * - `dataverse` — the discovery service, or with an environment URL that environment.
 *
 * Microsoft's default consent policy blocks user consent to the two Graph scopes
 * (40-client-apps-design.md §11.1): in most tenants they need the customer's admin.
 */
export const MICROSOFT_CONNECT_PURPOSES = ['files', 'sites', 'dataverse'] as const
export type MicrosoftConnectPurpose = (typeof MICROSOFT_CONNECT_PURPOSES)[number]

export function isMicrosoftConnectPurpose(value: unknown): value is MicrosoftConnectPurpose {
  return typeof value === 'string' && (MICROSOFT_CONNECT_PURPOSES as readonly string[]).includes(value)
}

/**
 * What a Microsoft connection made in `tid` can be used for. A personal account (the
 * consumer tid) has OneDrive but no SharePoint sites and no Dataverse, so only `files`;
 * an organisation, or a connection not made yet (null), all three.
 */
export function microsoftPurposesFor(tid: string | null | undefined): MicrosoftConnectPurpose[] {
  if (typeof tid === 'string' && tid.trim().toLowerCase() === MICROSOFT_PERSONAL_TENANT_ID) return ['files']
  return [...MICROSOFT_CONNECT_PURPOSES]
}

export const GRAPH_FILES_SCOPE = `${GRAPH_ORIGIN}/Files.ReadWrite.All`
export const GRAPH_SITES_SCOPE = `${GRAPH_ORIGIN}/Sites.ReadWrite.All`
export const DATAVERSE_DISCOVERY_SCOPE = `${DATAVERSE_DISCOVERY_ORIGIN}/user_impersonation`

/** Delegated Dataverse access to one environment, as the signed-in person (their security roles apply). */
export function dataverseEnvironmentScope(environmentUrl: string): string {
  return `${environmentUrl}/user_impersonation`
}

/**
 * The data scopes a purpose asks for — always fully qualified, so a stored scope list
 * names its resource. Null when `dataverse` names an environment that is not one
 * (DATAVERSE_ENVIRONMENT_URL): nothing is requested for a URL we would refuse to send
 * the worker.
 */
export function microsoftPurposeScopes(purpose: MicrosoftConnectPurpose, environmentUrl?: string | null): string[] | null {
  if (purpose === 'files') return [GRAPH_FILES_SCOPE]
  if (purpose === 'sites') return [GRAPH_SITES_SCOPE]
  if (environmentUrl === undefined || environmentUrl === null || environmentUrl === '') return [DATAVERSE_DISCOVERY_SCOPE]
  const url = normalizeDataverseEnvironmentUrl(environmentUrl)
  return url ? [dataverseEnvironmentScope(url)] : null
}

// =============================================================================
// Dataverse environment URLs
// =============================================================================

/**
 * A Dataverse environment's URL in the commercial cloud: `https://<org>.crm[N].dynamics.com`.
 * Checked before a token is requested for it or it is sent to the generation worker,
 * whose host allowlist mirrors this rule — a URL a discovery answer or a request body
 * names is never fetched with a customer's token unless it is one. Sovereign clouds
 * (`.dynamics.us`, `.dynamics.cn`, …) would be added here if ever needed.
 */
export const DATAVERSE_ENVIRONMENT_URL = /^https:\/\/[a-z0-9-]+\.crm[0-9]*\.dynamics\.com$/

/**
 * The environment URL in its one keyed spelling (`https://org.crm4.dynamics.com`:
 * lowercase, no trailing slash, nothing else), or null. Discovery answers with a
 * trailing slash; a person may paste one with a path. A path, query, port or user
 * part is refused rather than dropped: it is not an environment URL.
 */
export function normalizeDataverseEnvironmentUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > 300) return null
  let url: URL
  try {
    url = new URL(value.trim())
  } catch {
    return null
  }
  if (url.protocol !== 'https:' || url.username || url.password || url.port || url.search || url.hash) return null
  if (url.pathname !== '/' && url.pathname !== '') return null
  const normalized = `https://${url.hostname.toLowerCase()}`
  return DATAVERSE_ENVIRONMENT_URL.test(normalized) ? normalized : null
}

// =============================================================================
// Who is an admin (§3.2)
// =============================================================================

/**
 * Entra built-in roles whose holders administer their organisation's AutomaDoc account
 * (role *template* ids, which is what `wids` carries). Read from the ID token's `wids`
 * claim, which Microsoft includes once the app sets `groupMembershipClaims` to include
 * "DirectoryRole" (LIVE CHECK §12 B: that it is present in v2 ID and access tokens).
 */
export const ENTRA_ADMIN_ROLES = {
  globalAdministrator: '62e90394-69f5-4237-9190-012177145e10',
  privilegedRoleAdministrator: 'e8611ab8-c189-46e8-94e1-60213ab1f814',
  applicationAdministrator: '9b895d92-2cd3-44c7-9d02-a6ac2d5ea5c3',
  cloudApplicationAdministrator: '158c047a-c907-4556-b7ef-446551a6b5f7',
} as const

export const ENTRA_ADMIN_ROLE_IDS: readonly string[] = Object.values(ENTRA_ADMIN_ROLES)

/**
 * Our own app role (Enterprise applications → AutomaDoc → Users and groups): Entra's
 * idiomatic equivalent of Lark's app_admin list, assigned by the customer's IT.
 */
export const AUTOMADOC_ADMIN_APP_ROLE = 'AutomaDoc.Admin'

/** The delegated scope our API exposes to the Office add-ins and Teams (NAA). */
export const MICROSOFT_ACCESS_AS_USER_SCOPE = 'access_as_user'

/**
 * Whether a token's claims say this person administers their organisation: an admin
 * role in `wids`, or our app role in `roles`. A guest (`idp` ≠ `iss`) never is — they
 * administer some other organisation, if any (§3.2). Members promoted in AutomaDoc are
 * read from tenant_members by the API, not here.
 */
export function isMicrosoftPlatformAdmin(claims: { wids?: unknown; roles?: unknown }, options: { guest: boolean }): boolean {
  if (options.guest) return false
  const wids = Array.isArray(claims.wids) ? claims.wids : []
  const roles = Array.isArray(claims.roles) ? claims.roles : []
  return (
    wids.some((id) => typeof id === 'string' && ENTRA_ADMIN_ROLE_IDS.includes(id.toLowerCase())) ||
    roles.some((role) => role === AUTOMADOC_ADMIN_APP_ROLE)
  )
}

/**
 * Personal Microsoft accounts' tenant. Never an organisation: a token from it signs in to
 * an email account by its address, and connects OneDrive files only (no SharePoint, no
 * Dataverse: microsoftPurposesFor()).
 */
export function isMicrosoftPersonalTenant(tid: unknown): boolean {
  return typeof tid === 'string' && tid.trim().toLowerCase() === MICROSOFT_PERSONAL_TENANT_ID
}

// =============================================================================
// URLs
// =============================================================================

/**
 * The browser authorization step: authorization code with PKCE (S256) and a nonce.
 *
 * `tenant` is `organizations` for a sign-in; for a data connection the person's own tid,
 * so the account picker cannot hand back someone else's grant (the API still checks:
 * CONNECTION_USER_MISMATCH). The code must later be redeemed against the same tenant's
 * token endpoint with the same `redirectUri`.
 */
export function buildMicrosoftAuthorizeUrl(params: {
  clientId: string
  redirectUri: string
  /** Opaque anti-CSRF value echoed back on the redirect (`ms.<random>` by convention). */
  state: string
  nonce: string
  /** base64url(SHA-256(verifier)). */
  codeChallenge: string
  scopes: readonly string[]
  tenant?: string
  loginHint?: string
  /** `select_account` to offer a switch; nothing by default (Microsoft decides). */
  prompt?: 'select_account' | 'login' | 'consent'
}): string {
  const url = new URL(microsoftAuthorizeEndpoint(params.tenant ?? MICROSOFT_SIGNIN_AUTHORITY))
  url.searchParams.set('client_id', params.clientId)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('redirect_uri', params.redirectUri)
  // query, not fragment: the code goes to a page that posts it to the API; a fragment
  // would also work but query is what a Web-platform redirect URI is registered for.
  url.searchParams.set('response_mode', 'query')
  url.searchParams.set('scope', params.scopes.join(' '))
  url.searchParams.set('state', params.state)
  url.searchParams.set('nonce', params.nonce)
  url.searchParams.set('code_challenge', params.codeChallenge)
  url.searchParams.set('code_challenge_method', 'S256')
  if (params.loginHint) url.searchParams.set('login_hint', params.loginHint)
  if (params.prompt) url.searchParams.set('prompt', params.prompt)
  return url.toString()
}

/**
 * The page a customer's admin opens to consent for the whole organisation, when user
 * consent is not allowed (AADSTS65001 / 90094). `tenant` is the tid when known, else
 * `organizations` (the admin then signs in to their own). Microsoft redirects back with
 * `admin_consent=True&tenant=…&state=…` or an error; the returned `tenant` is never
 * trusted for authorization — the person still connects normally afterwards.
 */
export function buildAdminConsentUrl(params: {
  clientId: string
  tenant: string
  scopes: readonly string[]
  redirectUri: string
  state: string
}): string {
  const url = new URL(`${MICROSOFT_LOGIN_ORIGIN}/${params.tenant}/v2.0/adminconsent`)
  url.searchParams.set('client_id', params.clientId)
  url.searchParams.set('scope', params.scopes.join(' '))
  url.searchParams.set('redirect_uri', params.redirectUri)
  url.searchParams.set('state', params.state)
  return url.toString()
}

/**
 * The `state` the API signs into an admin-consent link (apps/api/worker/src/services/
 * microsoft/consent.ts). Microsoft hands it back on the portal root with
 * `admin_consent=True&tenant=…` or an error; the portal recognises it by this prefix and
 * forwards the answer to POST /api/auth/microsoft/admin-consent instead of treating it as
 * one of its own round trips (whose states are `ms.<random>`, portalSignIn.ts). The admin
 * who follows the link is usually someone else, in another browser, with no round trip
 * of their own waiting.
 */
export const MICROSOFT_ADMIN_CONSENT_STATE_PREFIX = 'msac.'

export function isMicrosoftAdminConsentState(state: unknown): state is string {
  return typeof state === 'string' && state.startsWith(MICROSOFT_ADMIN_CONSENT_STATE_PREFIX) && state.length <= 1024
}

// =============================================================================
// Approval for a whole organisation (the IT admin page)
// =============================================================================

/**
 * The state of an approval link nobody in particular asked for: the IT admin page
 * (portal `#/microsoft-admin`, docs `connect/microsoft-365/admin-consent`). Those pages
 * are static, so the state is a fixed marker rather than one the API signed. That is
 * enough because the answer grants nothing on our side (people still sign in and
 * connect normally, and their own tokens prove what they may do): the portal reads it
 * only to thank the admin, and never posts it to the API. It carries the `msac.` prefix
 * so the portal routes it with the API's own admin-consent returns.
 */
export const MICROSOFT_ORGANISATION_APPROVAL_STATE = `${MICROSOFT_ADMIN_CONSENT_STATE_PREFIX}org`

/**
 * What the IT admin page approves, as two links:
 * - `microsoft365`: sign-in and the two Microsoft Graph scopes (Excel workbooks,
 *   SharePoint lists). One resource besides the OpenID scopes, which every tenant has.
 * - `dataverse`: sign-in and Dynamics CRM's `user_impersonation`, separately, because
 *   Microsoft refuses consent to a resource the organisation has no service principal
 *   for (AADSTS650052), which is every organisation without Dataverse: folded into the
 *   first link it would break the approval for them.
 * Our own `access_as_user` is not asked: the add-ins fall back to the dialog sign-in,
 * which the sign-in scopes cover, and it is "admins and users" consentable.
 */
export const MICROSOFT_ORGANISATION_APPROVALS = {
  microsoft365: [...MICROSOFT_SIGNIN_SCOPES, GRAPH_FILES_SCOPE, GRAPH_SITES_SCOPE],
  dataverse: [...MICROSOFT_SIGNIN_SCOPES, DATAVERSE_DISCOVERY_SCOPE],
} as const satisfies Record<string, readonly string[]>
type MicrosoftOrganisationApproval = keyof typeof MICROSOFT_ORGANISATION_APPROVALS

const TENANT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** A directory (tenant) id as Entra writes it, lowercased; null for anything else, the personal-account tenant included. */
function organisationTenantId(value: unknown): string | null {
  if (typeof value !== 'string' || !TENANT_ID.test(value.trim())) return null
  const tid = value.trim().toLowerCase()
  return tid === MICROSOFT_PERSONAL_TENANT_ID ? null : tid
}

/**
 * Microsoft's admin-consent page for one of the IT admin page's approvals, for the
 * environment's own app: its client id (the caller's env config: the API's providers
 * answer, the docs' PUBLIC_MS_CLIENT_ID) and its portal root as the return address,
 * which is registered on that app and on no other. `tenant` is a directory id when the
 * page was opened from a refusal that named one, else `organizations` (the admin signs
 * in to their own).
 */
export function microsoftOrganisationApprovalUrl(params: {
  clientId: string
  environment: ClientEnvironment
  approval: MicrosoftOrganisationApproval
  tenant?: string | null
}): string {
  return buildAdminConsentUrl({
    clientId: params.clientId,
    tenant: organisationTenantId(params.tenant) ?? MICROSOFT_ORGANIZATIONS_AUTHORITY,
    scopes: MICROSOFT_ORGANISATION_APPROVALS[params.approval],
    redirectUri: microsoftPortalRedirectUri(params.environment),
    state: MICROSOFT_ORGANISATION_APPROVAL_STATE,
  })
}

/** The portal's public IT admin page (rendered signed out too). */
export const MICROSOFT_ADMIN_PAGE_PATH = '/microsoft-admin'

/**
 * The IT admin page's address to forward, with the directory of the refusal that led
 * there when the API's admin-consent link names one, so the admin's approval lands in
 * the right organisation even if they administer several.
 */
export function microsoftAdminPageUrl(portalOrigin: string, adminConsentUrl?: string | null): string {
  const base = `${portalOrigin.replace(/\/+$/, '')}/#${MICROSOFT_ADMIN_PAGE_PATH}`
  const tenant = tenantOfAdminConsentUrl(adminConsentUrl)
  return tenant ? `${base}?tenant=${tenant}` : base
}

/** The directory id in an admin-consent link's path (`/<tid>/v2.0/adminconsent`), or null. */
export function tenantOfAdminConsentUrl(url: unknown): string | null {
  if (typeof url !== 'string' || !url.startsWith(`${MICROSOFT_LOGIN_ORIGIN}/`)) return null
  try {
    return organisationTenantId(new URL(url).pathname.split('/')[1])
  } catch {
    return null
  }
}

/**
 * Every Microsoft permission AutomaDoc asks for, and the step that asks it: what the IT
 * admin page lists. Built from the same constants the sign-in and connect code sends,
 * so the list cannot fall behind them (microsoft.test.ts checks it covers each).
 */
export type MicrosoftPermissionStep = 'signin' | 'addins' | 'files' | 'sites' | 'dataverse'

export interface MicrosoftPermission {
  /** The name Microsoft's consent screen and the Entra admin center show. */
  name: string
  /** The API it belongs to, as Entra names it. */
  api: 'OpenID Connect' | 'Microsoft Graph' | 'AutomaDoc' | 'Dynamics CRM'
  /** The full scope string sent to Microsoft. */
  scope: string
  step: MicrosoftPermissionStep
}

export function microsoftPermissions(environment: ClientEnvironment, clientId: string): MicrosoftPermission[] {
  const signin = MICROSOFT_SIGNIN_SCOPES.map((scope): MicrosoftPermission => ({ name: scope, api: 'OpenID Connect', scope, step: 'signin' }))
  return [
    ...signin,
    {
      name: MICROSOFT_ACCESS_AS_USER_SCOPE,
      api: 'AutomaDoc',
      scope: `${microsoftApplicationIdUri(environment, clientId)}/${MICROSOFT_ACCESS_AS_USER_SCOPE}`,
      step: 'addins',
    },
    { name: 'Files.ReadWrite.All', api: 'Microsoft Graph', scope: GRAPH_FILES_SCOPE, step: 'files' },
    { name: 'Sites.ReadWrite.All', api: 'Microsoft Graph', scope: GRAPH_SITES_SCOPE, step: 'sites' },
    { name: 'user_impersonation', api: 'Dynamics CRM', scope: DATAVERSE_DISCOVERY_SCOPE, step: 'dataverse' },
  ]
}

// =============================================================================
// Redirect URIs per environment (what each Entra app registration must list)
// =============================================================================

function hostOf(uri: string): string {
  return new URL(uri).host
}

function redirectUrisFor(environment: ClientEnvironment): { web: readonly string[]; spa: readonly string[] } {
  const officeHost = hostOf(clientRedirectUri(environment, 'excel'))
  const portalHost = hostOf(clientRedirectUri(environment, 'teams'))
  return {
    // Confidential (certificate) code redemption: the portal's own sign-in, connect and
    // admin-consent return (all on the portal root, told apart by the state's prefix),
    // and the client apps' server-held attempts (40 §1.3).
    web: [`https://${portalHost}/`, clientRedirectUri(environment, 'excel'), clientRedirectUri(environment, 'teams')],
    // Nested app authentication in the Office add-ins and the Teams tab (40 §2.3 step 5).
    // The portal root is deliberately *not* here although 40 §2.3 lists it for Teams:
    // it is already a Web redirect, and one URI cannot be registered under two platform
    // types (LIVE CHECK §12 B). NAA itself only needs the brk-multihub:// entries.
    spa: [
      `brk-multihub://${officeHost}`,
      `https://${officeHost}/excel.html`,
      `https://${officeHost}/word.html`,
      `brk-multihub://${portalHost}`,
    ],
  }
}

export const MICROSOFT_REDIRECT_URIS: Record<ClientEnvironment, { web: readonly string[]; spa: readonly string[] }> = {
  staging: redirectUrisFor('staging'),
  production: redirectUrisFor('production'),
}

/** The portal's sign-in / connect / admin-consent return address in an environment. */
export function microsoftPortalRedirectUri(environment: ClientEnvironment): string {
  return MICROSOFT_REDIRECT_URIS[environment].web[0]
}

/** The company portal's dev server; registered on the staging app only, accepted only by a development Worker. */
export const MICROSOFT_DEV_PORTAL_REDIRECT_URI = 'http://localhost:3004/'

/**
 * The Application ID URI of the environment's app (Expose an API). Fixed from day one:
 * installed add-ins name it in their token requests, so changing it later breaks them —
 * a new one can only be *added* (identifierUris is a list).
 */
export function microsoftApplicationIdUri(environment: ClientEnvironment, clientId: string): string {
  return `api://${apiHostOf(environment)}/${clientId}`
}

// =============================================================================
// Errors
// =============================================================================

/** The `AADSTS<n>` code in an error description, or null. Never the description itself (it can echo input). */
export function aadstsCode(text: unknown): string | null {
  if (typeof text !== 'string') return null
  const match = /AADSTS(\d{4,7})/.exec(text)
  return match ? `AADSTS${match[1]}` : null
}

/**
 * Codes that mean "an admin must consent first": the user may not consent to these
 * scopes (65001), risk-based step-up turned the user's consent into an admin request
 * (90094), or the request was filed through the admin consent workflow (90095).
 */
export const MICROSOFT_CONSENT_REQUIRED_CODES: readonly string[] = ['AADSTS65001', 'AADSTS90094', 'AADSTS90095']

export function isMicrosoftConsentRequired(error: { error?: unknown; errorDescription?: unknown }): boolean {
  const code = aadstsCode(error.errorDescription)
  if (code && MICROSOFT_CONSENT_REQUIRED_CODES.includes(code)) return true
  return error.error === 'consent_required'
}

// =============================================================================
// API shapes (handlers/microsoftAuth.ts)
// =============================================================================

/**
 * POST /api/auth/microsoft/login — the portal posts what came back on its redirect:
 * the code with its verifier and nonce, or Microsoft's error (then MS_CONSENT_REQUIRED
 * carries the admin-consent link when that is the cause). Answered with a
 * ClientSessionResponse (clientApps.ts): the same session shape every non-Lark sign-in
 * returns.
 */
export interface MicrosoftLoginRequest {
  code?: string
  redirectUri?: string
  codeVerifier?: string
  nonce?: string
  error?: string
  errorDescription?: string
  locale?: string
  /** An invitation into a Microsoft 365 account, accepted if this person is of that Entra tenant. */
  invitation?: import('./emailAuth').InvitationProof
}

/** 403 MS_CONSENT_REQUIRED */
export interface MicrosoftConsentRequiredBody {
  error: string
  code: 'MS_CONSENT_REQUIRED'
  adminConsentUrl: string
}

/** POST /api/tenant/connections/microsoft */
export interface MicrosoftConnectRequest {
  purpose: MicrosoftConnectPurpose
  /** With purpose 'dataverse': the environment to connect; without, the discovery service. */
  environmentUrl?: string
  /** A client app (excel, word, teams): the API holds the attempt and answers with the URL. */
  client?: 'excel' | 'word' | 'teams'
}

/**
 * Its answer. With `client`, the URL to open (server-held state). Without, what the
 * portal puts in its own authorize URL (PKCE state in sessionStorage, as its sign-in
 * does; redirect URI its own root) before posting the code to /complete: the authority
 * (a Microsoft 365 session's own tid; for anyone else the tid of the Microsoft identity
 * they connected before, or `common` to choose one) and the scopes to ask for.
 */
export type MicrosoftConnectResponse =
  | { authorizeUrl: string; expiresAt: number }
  | { tenant: string; scopes: string[] }

/** POST /api/tenant/connections/microsoft/complete (the portal's return leg) */
export interface MicrosoftConnectCompleteRequest extends MicrosoftLoginRequest {
  purpose: MicrosoftConnectPurpose
  environmentUrl?: string
}

/** The portal's /complete and a client's client-complete for a connect attempt. */
export interface MicrosoftConnectedResponse extends ClientConnectResult {
  purpose: MicrosoftConnectPurpose
}

/** GET /api/tenant/connections/microsoft — the caller's own connection, never a token. */
export interface MicrosoftConnectionStatus {
  connected: boolean
  status: 'active' | 'reauth_required' | 'revoked' | null
  /**
   * The purposes this connection can offer: all three for a work or school account (or
   * none connected yet), only `files` for a personal Microsoft account. Show only these.
   */
  availablePurposes?: MicrosoftConnectPurpose[]
  /** The Microsoft identity the grant was made as, for "Connected as …" (null before migration 0076's grants). */
  account?: { tenantId: string; personal: boolean; address: string | null } | null
  purposes: Record<MicrosoftConnectPurpose, boolean>
  /** Dataverse environments this person has connected. */
  dataverseEnvironments: string[]
  lastUsedAt: number | null
}

export interface DataverseEnvironment {
  url: string
  name: string
  uniqueName: string | null
  region: string | null
  environmentId: string | null
}

/** GET /api/tenant/connections/microsoft/dataverse/environments */
export interface DataverseEnvironmentsResponse {
  environments: DataverseEnvironment[]
}

/** POST /api/auth/microsoft/admin-consent — the portal forwards Microsoft's return. */
export interface MicrosoftAdminConsentResultRequest {
  state: string
  adminConsent?: string
  tenant?: string
  error?: string
  errorDescription?: string
}

export interface MicrosoftAdminConsentResultResponse {
  granted: boolean
  purpose: MicrosoftConnectPurpose | 'signin'
  /** The AADSTS code when Microsoft refused; never its description. */
  errorCode?: string | null
}
